import { useState } from 'react';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, CalendarClock, Mail, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { useTenant } from '@/context/TenantContext';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { downloadPost } from '@/lib/download';
import { fmtDate } from '@/lib/format';
import { useGet } from '@/lib/useData';
import api from '@/lib/api';

// --- ISO week helpers (semaine par semaine) — mêmes règles que le back-office. ---
function _isoWeekMonday(year, week) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = (jan4.getUTCDay() + 6) % 7; // Mon=0
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Dow + (week - 1) * 7);
  return monday;
}
function _isoWeekOf(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return { year: d.getUTCFullYear(), week };
}
function _weekOptions(refYear, refWeek, count = 24) {
  const opts = [];
  const refMonday = _isoWeekMonday(refYear, refWeek);
  const fmt = (dt) => dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  for (let i = 0; i < count; i++) {
    const monday = new Date(refMonday);
    monday.setUTCDate(refMonday.getUTCDate() - i * 7);
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    const { year, week } = _isoWeekOf(monday);
    opts.push({ value: `${year}-${week}`, year, week, label: `S${week} ${year} · ${fmt(monday)} – ${fmt(sunday)}` });
  }
  return opts;
}

const CARDS = [
  { key: 'excel', ext: 'xlsx', icon: FileSpreadsheet, title: 'Rapport Excel', desc: 'Reporting complet : cumul annuel, récapitulatif par mission, vue hebdomadaire et détail jour par jour de chaque code campagne.', badge: 'Détaillé' },
  { key: 'pdf', ext: 'pdf', icon: FileText, title: 'Rapport PDF', desc: 'Synthèse de pilotage : indicateurs clés, objectifs, répartition par âge et performances par code campagne.', badge: 'Synthèse' },
];

function AmnestyWeeklyReport({ isAdmin }) {
  const info = useGet('/reports/amnesty/current-week');
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [weekSel, setWeekSel] = useState('');
  const meta = info.data;

  // Liste des semaines sélectionnables (récentes d'abord), à partir de la semaine ciblée.
  const weekOpts = (meta?.year && meta?.week) ? _weekOptions(meta.year, meta.week, 24) : [];
  // Valeur courante : sélection utilisateur, sinon la semaine écoulée par défaut.
  const currentValue = weekSel || (meta?.year && meta?.week ? `${meta.year}-${meta.week}` : '');
  const selIndex = weekOpts.findIndex((o) => o.value === currentValue);
  const selected = weekOpts[selIndex] || (meta?.year ? { year: meta.year, week: meta.week } : null);

  const goOlder = () => { const o = weekOpts[selIndex + 1]; if (o) setWeekSel(o.value); };
  const goNewer = () => { const o = weekOpts[selIndex - 1]; if (o) setWeekSel(o.value); };

  const download = async () => {
    if (!selected) return;
    setDownloading(true);
    try {
      const res = await api.get('/reports/amnesty/weekly',
        { params: { year: selected.year, week: selected.week }, responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m ? m[1] : `REPORTING SHARITI - AMNESTY ${selected.year} - S${selected.week}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = fname;
      document.body.appendChild(link); link.click();
      link.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Reporting S${selected.week} ${selected.year} téléchargé`);
    } catch (e) {
      toast.error('Génération impossible');
    } finally { setDownloading(false); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await api.post('/reports/amnesty/email-test', selected ? { year: selected.year, week: selected.week } : {});
      toast.success(`Email test envoyé à ${res.data?.to}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Échec de l'envoi";
      toast.error(msg);
    } finally { setSending(false); }
  };

  const selMonday = selected ? _isoWeekMonday(selected.year, selected.week) : null;
  const selSunday = selMonday ? new Date(selMonday.getTime() + 6 * 86400000) : null;
  const fmtUTC = (dt) => dt ? dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '';

  return (
    <div className="rounded-2xl border bg-card p-6 card-shadow" data-testid="amnesty-weekly-report">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl accent-bg-100">
            <CalendarClock className="h-6 w-6 accent-text" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-semibold">Reporting hebdo Amnesty</h3>
              <Badge variant="secondary" className="text-[10px]">Amnesty</Badge>
            </div>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Fichier au format habituel (par code campagne, récapitulatifs et graphiques). Choisissez
              la <strong className="text-foreground">semaine</strong> et téléchargez-la à la demande.{isAdmin && ' Envoi automatique prévu chaque mercredi à 14h30.'}
            </p>
          </div>
        </div>
        {selected && (
          <div className="rounded-xl accent-bg-soft px-3 py-2 text-right" data-testid="amnesty-week-badge">
            <div className="text-xs text-muted-foreground">Semaine sélectionnée</div>
            <div className="font-display text-base font-semibold accent-text">S{selected.week} · {selected.year}</div>
            <div className="text-[11px] text-muted-foreground">{fmtUTC(selMonday)} → {fmtUTC(selSunday)}</div>
          </div>
        )}
      </div>

      {/* Navigation semaine par semaine */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" className="rounded-xl" onClick={goOlder}
          disabled={info.isLoading || selIndex >= weekOpts.length - 1} aria-label="Semaine précédente"
          data-testid="amnesty-week-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Select value={currentValue} onValueChange={setWeekSel} disabled={info.isLoading || weekOpts.length === 0}>
          <SelectTrigger className="w-[280px] rounded-xl" data-testid="amnesty-week-select">
            <SelectValue placeholder="Choisir une semaine" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {weekOpts.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`amnesty-week-opt-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="rounded-xl" onClick={goNewer}
          disabled={info.isLoading || selIndex <= 0} aria-label="Semaine suivante"
          data-testid="amnesty-week-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button className="gap-2 rounded-xl text-white" style={{ background: 'var(--tenant-accent-500)' }}
          onClick={download} disabled={downloading || info.isLoading || !selected} data-testid="amnesty-download-btn">
          {downloading ? 'Génération…' : <><Download className="h-4 w-4" /> Télécharger cette semaine</>}
        </Button>

        {isAdmin && (
          <>
            <Button variant="outline" className="gap-2 rounded-xl"
              onClick={sendTest} disabled={sending || !meta?.email_configured} data-testid="amnesty-email-test-btn">
              {sending ? 'Envoi…' : <><Send className="h-4 w-4" /> Envoyer un test</>}
            </Button>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Mail className="h-3.5 w-3.5" />
              {meta?.email_configured
                ? <>Test envoyé à <strong className="text-foreground">{meta?.test_recipient}</strong></>
                : <>Envoi email non configuré (clé Resend à ajouter)</>}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default function Exports() {
  const { body, period } = useFilters();
  const { association } = useTenant();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(null);
  const isAmnesty = association?.code === 'AMNESTY';
  // For the Amnesty association space, the weekly report already contains everything,
  // so the redundant detailed Excel export is hidden (admins keep it).
  const cards = (isAmnesty && !isAdmin) ? CARDS.filter((c) => c.key !== 'excel') : CARDS;

  const run = async (key, ext) => {
    setLoading(key);
    try {
      const fname = `SHARITI_${body.association}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      await downloadPost(`/exports/${key}`, body, fname);
      toast.success('Export généré');
    } catch (e) {
      toast.error('Export impossible');
    } finally { setLoading(null); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Exports" icon={Download} subtitle="Téléchargez vos rapports dans le format de votre choix" />

      <div className="rounded-2xl border bg-card p-4 text-sm text-muted-foreground card-shadow">
        Périmètre actuel : <strong className="text-foreground">{association?.name}</strong>
        {period?.start && <> · du <strong className="text-foreground">{fmtDate(period.start)}</strong> au <strong className="text-foreground">{fmtDate(period.end)}</strong></>}
        {body.campaign_codes?.length > 0 && <> · {body.campaign_codes.length} code(s) filtré(s)</>}
      </div>

      {isAmnesty && <AmnestyWeeklyReport isAdmin={isAdmin} />}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {cards.map((c) => (
          <div key={c.key} className="flex flex-col rounded-2xl border bg-card p-6 card-shadow" data-testid={`export-card-${c.key}`}>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl accent-bg-100"><c.icon className="h-6 w-6 accent-text" /></div>
            <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
            <span className="mt-1 w-fit rounded-full accent-bg-soft px-2 py-0.5 text-[10px] font-medium accent-text">{c.badge}</span>
            <p className="mt-3 flex-1 text-sm text-muted-foreground">{c.desc}</p>
            <Button className="mt-4 gap-2 rounded-xl text-white" style={{ background: 'var(--tenant-accent-500)' }}
              onClick={() => run(c.key, c.ext)} disabled={loading === c.key} data-testid={`export-btn-${c.key}`}>
              {loading === c.key ? 'Génération…' : <><Download className="h-4 w-4" /> Télécharger</>}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
